import * as plugins from './plugins.js';
import {
  VirtualStream,
  VirtualStreamActivationScope,
} from './classes.virtualstream.js';

import { TypedHandler } from './classes.typedhandler.js';
import { TypedRequest } from './classes.typedrequest.js';
import { TypedResponseError } from './classes.typedresponseerror.js';
import {
  addGlobalHookSubscriber,
  clearGlobalHookRegistrations,
  createTypedRouterHookRegistrationState,
  dispatchTypedRequestHook,
  getLegacyGlobalHooks,
  mergeLegacyGlobalHooks,
  mergeTypedRouterHookRegistrations,
  replaceLegacyGlobalHooks,
  replaceTypedRouterHookRegistrations,
} from './internal.hooks.js';
import type {
  ITypedRouterHookProjectionOptions,
  ITypedRouterHooks,
} from './interfaces.hooks.js';

export {
  TYPED_REQUEST_REDACTED_PAYLOAD,
} from './interfaces.hooks.js';
export type {
  ITypedRequestLogEntry,
  ITypedRequestLogMetadata,
  ITypedRouterHookProjectionOptions,
  ITypedRouterHooks,
  TTypedRouterHookCallback,
} from './interfaces.hooks.js';

/**
 * Middleware function that runs before the handler.
 * Throw TypedResponseError to reject the request.
 */
export type TMiddlewareFunction<TReq extends Partial<plugins.typedRequestInterfaces.ITypedRequest> = plugins.typedRequestInterfaces.ITypedRequest> = (
  typedRequest: plugins.typedRequestInterfaces.ITypedRequest & TReq
) => Promise<void>;

export interface ITypedRouterRouteOptions {
  localRequest?: boolean;
  skipHooks?: boolean;
  /**
   * Server-owned transport data applied after network payload decoding.
   * Caller-supplied localData is always discarded completely.
   */
  trustedLocalData?: Record<string, unknown>;
  /** Transport-owned cancellation, kept separate from wire and local data. */
  trustedAbortSignal?: AbortSignal;
}

export interface ITypedRouterIncomingResponseAuthorityContext {
  readonly typedRouter: TypedRouter<any>;
  readonly trustedLocalData: Readonly<Record<string, unknown>>;
  /** Whether the raw envelope had its own enumerable localData property. */
  readonly wireLocalDataPresent: boolean;
}

export type TTypedRouterIncomingResponseAuthorityGuard = (
  typedRequestArg: Readonly<plugins.typedRequestInterfaces.ITypedRequest>,
  contextArg: Readonly<ITypedRouterIncomingResponseAuthorityContext>
) => boolean;

export interface ITypedRouterIncomingRequestAuthorityContext {
  readonly typedRouter: TypedRouter<any>;
  readonly trustedLocalData: Readonly<Record<string, unknown>>;
  /** Whether the raw envelope had its own enumerable localData property. */
  readonly wireLocalDataPresent: boolean;
}

export type TTypedRouterIncomingRequestAuthorityGuard = (
  typedRequestArg: Readonly<plugins.typedRequestInterfaces.ITypedRequest>,
  contextArg: Readonly<ITypedRouterIncomingRequestAuthorityContext>
) => boolean;

export interface ITypedRouterRequestSignalRegistration {
  readonly abortSignal: AbortSignal;
  complete(): void;
}

export type TTypedRouterIncomingRequestSignalResolver = (
  typedRequestArg: Readonly<plugins.typedRequestInterfaces.ITypedRequest>,
  contextArg: Readonly<ITypedRouterIncomingRequestAuthorityContext>
) => ITypedRouterRequestSignalRegistration | undefined;

export function getTypedRequestResponseInterestKey(
  typedRequestArg: Readonly<plugins.typedRequestInterfaces.ITypedRequest>,
): string | undefined {
  const method = typedRequestArg?.method;
  const correlationId = typedRequestArg?.correlation?.id;
  const requestInstanceId = typedRequestArg?.requestInstanceId;
  if (
    typeof method !== 'string'
    || method.length === 0
    || typeof correlationId !== 'string'
    || correlationId.length === 0
    || typeof requestInstanceId !== 'string'
    || requestInstanceId.length === 0
  ) return undefined;
  return JSON.stringify([method, correlationId, requestInstanceId]);
}

export type TVirtualStreamTransportResolver = (
  trustedLocalDataArg: Readonly<Record<string, unknown>>
) => plugins.typedRequestInterfaces.IVirtualStreamTransport | undefined;

/**
 * A typed router decides on which typed handler to call based on the method
 * specified in the typed request
 * This is thought for reusing the same url endpoint for different methods
 */
export class TypedRouter<TReqConstraint extends Partial<plugins.typedRequestInterfaces.ITypedRequest> = plugins.typedRequestInterfaces.ITypedRequest> {
  private static readonly internalErrorMessage = 'Internal server error';
  private virtualStreamTransportResolver:
    | TVirtualStreamTransportResolver
    | undefined;
  private incomingRequestAuthorityGuard:
    | TTypedRouterIncomingRequestAuthorityGuard
    | undefined;
  private incomingResponseAuthorityGuard:
    | TTypedRouterIncomingResponseAuthorityGuard
    | undefined;
  private incomingRequestSignalResolver:
    | TTypedRouterIncomingRequestSignalResolver
    | undefined;
  /**
   * Installs one scoped owner for per-route VirtualStream transport resolution.
   * The resolver receives only trusted local transport data.
   */
  public registerVirtualStreamTransportResolver(
    resolverArg: TVirtualStreamTransportResolver
  ): () => void {
    if (typeof resolverArg !== 'function') {
      throw new TypeError('TypedRouter virtual stream transport resolver must be a function');
    }
    if (this.virtualStreamTransportResolver) {
      throw new Error(
        'TypedRouter already has a virtual stream transport resolver owner'
      );
    }
    this.virtualStreamTransportResolver = resolverArg;
    let detached = false;
    return () => {
      if (detached) return;
      detached = true;
      if (this.virtualStreamTransportResolver === resolverArg) {
        this.virtualStreamTransportResolver = undefined;
      }
    };
  }

  /** Installs one transport-owned guard for incoming requests. */
  public registerIncomingRequestAuthorityGuard(
    guardArg: TTypedRouterIncomingRequestAuthorityGuard
  ): () => void {
    if (typeof guardArg !== 'function') {
      throw new TypeError('TypedRouter incoming request authority guard must be a function');
    }
    if (this.incomingRequestAuthorityGuard) {
      throw new Error('TypedRouter already has an incoming request authority guard owner');
    }
    this.incomingRequestAuthorityGuard = guardArg;
    let detached = false;
    return () => {
      if (detached) return;
      detached = true;
      if (this.incomingRequestAuthorityGuard === guardArg) {
        this.incomingRequestAuthorityGuard = undefined;
      }
    };
  }

  /** Installs one transport owner for trusted request-scoped cancellation. */
  public registerIncomingRequestSignalResolver(
    resolverArg: TTypedRouterIncomingRequestSignalResolver
  ): () => void {
    if (typeof resolverArg !== 'function') {
      throw new TypeError('TypedRouter incoming request signal resolver must be a function');
    }
    if (this.incomingRequestSignalResolver) {
      throw new Error('TypedRouter already has an incoming request signal resolver owner');
    }
    this.incomingRequestSignalResolver = resolverArg;
    let detached = false;
    return () => {
      if (detached) return;
      detached = true;
      if (this.incomingRequestSignalResolver === resolverArg) {
        this.incomingRequestSignalResolver = undefined;
      }
    };
  }

  /** Installs one transport-owned guard for separately routed responses. */
  public registerIncomingResponseAuthorityGuard(
    guardArg: TTypedRouterIncomingResponseAuthorityGuard
  ): () => void {
    if (typeof guardArg !== 'function') {
      throw new TypeError('TypedRouter incoming response authority guard must be a function');
    }
    if (this.incomingResponseAuthorityGuard) {
      throw new Error('TypedRouter already has an incoming response authority guard owner');
    }
    this.incomingResponseAuthorityGuard = guardArg;
    let detached = false;
    return () => {
      if (detached) return;
      detached = true;
      if (this.incomingResponseAuthorityGuard === guardArg) {
        this.incomingResponseAuthorityGuard = undefined;
      }
    };
  }

  public static get globalHooks(): ITypedRouterHooks {
    return getLegacyGlobalHooks();
  }

  public static set globalHooks(value: ITypedRouterHooks) {
    replaceLegacyGlobalHooks(value);
  }

  /**
   * Set global hooks for monitoring all TypedRequest traffic
   * Hooks are shared across all bundles via globalThis
   */
  public static setGlobalHooks(
    hooks: ITypedRouterHooks,
    projectionOptions?: ITypedRouterHookProjectionOptions
  ): void {
    mergeLegacyGlobalHooks(hooks, projectionOptions);
  }

  /**
   * Add an independently projected global hook subscriber.
   */
  public static addGlobalHooks(
    hooks: ITypedRouterHooks,
    projectionOptions?: ITypedRouterHookProjectionOptions
  ): () => void {
    return addGlobalHookSubscriber(hooks, projectionOptions);
  }

  /**
   * Clear all global hooks
   */
  public static clearGlobalHooks(): void {
    clearGlobalHookRegistrations();
  }

  private readonly hookRegistrationState =
    createTypedRouterHookRegistrationState();

  public get hooks(): ITypedRouterHooks {
    return this.hookRegistrationState.hooks;
  }

  public set hooks(value: ITypedRouterHooks) {
    replaceTypedRouterHookRegistrations(this.hookRegistrationState, value);
  }

  /**
   * Set instance-level hooks for monitoring traffic through this router
   */
  public setHooks(
    hooks: ITypedRouterHooks,
    projectionOptions?: ITypedRouterHookProjectionOptions
  ): void {
    mergeTypedRouterHookRegistrations(
      this.hookRegistrationState,
      hooks,
      projectionOptions
    );
  }

  /**
   * Helper to call both global and instance hooks
   */
  private callHook(
    hookName: keyof ITypedRouterHooks,
    source: Parameters<typeof dispatchTypedRequestHook>[1]
  ): void {
    dispatchTypedRequestHook(hookName, source, this.hookRegistrationState);
  }

  private setErrorFromCaughtError(
    typedRequestArg: plugins.typedRequestInterfaces.ITypedRequest,
    errorArg: unknown,
    contextArg: string,
  ): void {
    if (errorArg instanceof TypedResponseError) {
      typedRequestArg.error = { text: errorArg.errorText, data: errorArg.errorData ?? {} };
      return;
    }

    console.error(`TypedRequest ${contextArg} failed`);
    typedRequestArg.error = { text: TypedRouter.internalErrorMessage, data: {} };
  }

  public routerMap = new plugins.lik.ObjectMap<TypedRouter<any>>();
  /**
   * One-way fallback routers. A fallback can serve this router without being
   * able to route back into it.
   */
  private fallbackRouterMap = new plugins.lik.ObjectMap<TypedRouter<any>>();

  /**
   * Validation-only inbound links. They never participate in routing; they
   * let a composed parent reject collisions introduced by later mutations.
   */
  private fallbackParentRouters = new Set<TypedRouter<any>>();
  public handlerMap = new plugins.lik.ObjectMap<
    TypedHandler<any & plugins.typedRequestInterfaces.ITypedRequest>
  >();
  // Middleware chain
  private middlewares: TMiddlewareFunction<TReqConstraint>[] = [];

  /**
   * Adds a middleware function that runs before the handler on this router.
   * Throw TypedResponseError to reject the request.
   */
  public addMiddleware(fn: TMiddlewareFunction<TReqConstraint>): void {
    this.middlewares.push(fn);
  }

  public fireEventInterestMap = new plugins.lik.InterestMap<
    string,
    plugins.typedRequestInterfaces.ITypedRequest
  >((correlationId: string) => correlationId);

  /**
   * adds the handler to the routing map
   * @param typedHandlerArg
   */
  public addTypedHandler<T extends plugins.typedRequestInterfaces.ITypedRequest & TReqConstraint>(
    typedHandlerArg: TypedHandler<T>
  ) {
    // lets check for deduplication
    const existingTypedHandler = this.getTypedHandlerForMethod(typedHandlerArg.method);
    if (existingTypedHandler) {
      throw new Error(
        `a TypedHandler for ${typedHandlerArg.method} alredy exists! Can't add another one.`
      );
    }

    this.handlerMap.add(typedHandlerArg);
    try {
      this.validateAffectedCompositionRoots();
    } catch (error) {
      this.handlerMap.remove(typedHandlerArg);
      throw error;
    }
  }

  /**
   * Adds another router through a bidirectional edge.
   * Returns an idempotent disposer for the edge created by this call.
   */
  public addTypedRouter(typedRouterArg: TypedRouter<any>): () => void {
    if (typedRouterArg === this) {
      throw new Error('A TypedRouter cannot be composed with itself.');
    }
    const routerExists = this.routerMap.findSync((routerArg) => routerArg === typedRouterArg);
    if (routerExists) {
      return () => {};
    }
    const thisCanReachOther = this.canReachRouter(typedRouterArg);
    const otherCanReachThis = typedRouterArg.canReachRouter(this);
    if (thisCanReachOther !== otherCanReachThis) {
      throw new Error(
        'Cannot add a bidirectional TypedRouter edge across an existing composition boundary.',
      );
    }
    this.routerMap.add(typedRouterArg);
    typedRouterArg.routerMap.add(this);
    try {
      this.validateAffectedCompositionRoots();
    } catch (error) {
      this.routerMap.remove(typedRouterArg);
      typedRouterArg.routerMap.remove(this);
      throw error;
    }

    let detached = false;
    return () => {
      if (detached) {
        return;
      }
      detached = true;
      this.routerMap.remove(typedRouterArg);
      typedRouterArg.routerMap.remove(this);
    };
  }

  /**
   * Adds a router as a one-way fallback.
   *
   * Requests entering this router can reach the fallback router, while
   * requests entering the fallback router cannot reach this router. This is
   * useful for composing internal protocol handlers into multiple isolated
   * application routers.
   */
  public addFallbackRouter(typedRouterArg: TypedRouter<any>): () => void {
    if (typedRouterArg === this) {
      throw new Error('A TypedRouter cannot use itself as a fallback.');
    }
    if (this.fallbackRouterMap.findSync((routerArg) => routerArg === typedRouterArg)) {
      return () => {};
    }
    if (typedRouterArg.canReachRouter(this)) {
      throw new Error('Adding this fallback would create a TypedRouter composition cycle.');
    }

    this.fallbackRouterMap.add(typedRouterArg);
    typedRouterArg.fallbackParentRouters.add(this);
    try {
      this.validateAffectedCompositionRoots();
    } catch (error) {
      this.fallbackRouterMap.remove(typedRouterArg);
      typedRouterArg.fallbackParentRouters.delete(this);
      throw error;
    }

    let detached = false;
    return () => {
      if (detached) {
        return;
      }
      detached = true;
      this.fallbackRouterMap.remove(typedRouterArg);
      typedRouterArg.fallbackParentRouters.delete(this);
    };
  }

  /**
   * Returns the unique method names reachable from this router.
   */
  public getMethodNames(): string[] {
    return [...this.collectReachableHandlers().keys()].sort();
  }

  private canReachRouter(
    targetRouterArg: TypedRouter<any>,
    checkedRoutersArg = new Set<TypedRouter<any>>(),
  ): boolean {
    if (this === targetRouterArg) {
      return true;
    }
    if (checkedRoutersArg.has(this)) {
      return false;
    }
    checkedRoutersArg.add(this);
    for (const router of [
      ...this.routerMap.getArray(),
      ...this.fallbackRouterMap.getArray(),
    ]) {
      if (router.canReachRouter(targetRouterArg, checkedRoutersArg)) {
        return true;
      }
    }
    return false;
  }

  private collectReachableHandlers(
    checkedRoutersArg = new Set<TypedRouter<any>>(),
    ownersByMethodArg = new Map<string, Set<TypedRouter<any>>>(),
  ): Map<string, Set<TypedRouter<any>>> {
    if (checkedRoutersArg.has(this)) {
      return ownersByMethodArg;
    }
    checkedRoutersArg.add(this);

    for (const handler of this.handlerMap.getArray()) {
      let methodOwners = ownersByMethodArg.get(handler.method);
      if (!methodOwners) {
        methodOwners = new Set();
        ownersByMethodArg.set(handler.method, methodOwners);
      }
      methodOwners.add(this);
    }
    for (const router of [
      ...this.routerMap.getArray(),
      ...this.fallbackRouterMap.getArray(),
    ]) {
      router.collectReachableHandlers(checkedRoutersArg, ownersByMethodArg);
    }
    return ownersByMethodArg;
  }

  private getAffectedCompositionRoots(): Set<TypedRouter<any>> {
    const affectedRouters = new Set<TypedRouter<any>>();
    const pendingRouters: TypedRouter<any>[] = [this];
    while (pendingRouters.length > 0) {
      const router = pendingRouters.pop()!;
      if (affectedRouters.has(router)) {
        continue;
      }
      affectedRouters.add(router);
      pendingRouters.push(
        ...router.routerMap.getArray(),
        ...router.fallbackParentRouters,
      );
    }
    return affectedRouters;
  }

  private validateAffectedCompositionRoots(): void {
    for (const rootRouter of this.getAffectedCompositionRoots()) {
      for (const [method, owners] of rootRouter.collectReachableHandlers()) {
        if (owners.size > 1) {
          throw new Error(
            `TypedRouter composition exposes more than one handler for method "${method}".`,
          );
        }
      }
    }
  }

  public checkForTypedHandler(methodArg: string): boolean {
    return !!this.getTypedHandlerForMethod(methodArg);
  }

  /**
   * gets a typed Router from the router chain, upstream and downstream
   * @param methodArg
   * @param checkUpstreamRouter
   */
  public getTypedHandlerForMethod(
    methodArg: string,
    checkedRouters: TypedRouter<any>[] = []
  ): TypedHandler<any> {
    checkedRouters.push(this);

    let typedHandler: TypedHandler<any>;

    typedHandler = this.handlerMap.findSync((handler) => {
      return handler.method === methodArg;
    });

    if (!typedHandler) {
      this.routerMap.getArray().forEach((typedRouterArg) => {
        if (!typedHandler && !checkedRouters.includes(typedRouterArg)) {
          typedHandler = typedRouterArg.getTypedHandlerForMethod(methodArg, checkedRouters);
        }
      });
    }

    if (!typedHandler) {
      this.fallbackRouterMap.getArray().forEach((typedRouterArg) => {
        if (!typedHandler && !checkedRouters.includes(typedRouterArg)) {
          typedHandler = typedRouterArg.getTypedHandlerForMethod(methodArg, checkedRouters);
        }
      });
    }

    return typedHandler;
  }

  /**
   * Finds the handler AND its owning router for a given method.
   * Needed to know which router's middleware to run.
   */
  public getTypedHandlerAndRouter(
    methodArg: string,
    checkedRouters: TypedRouter<any>[] = []
  ): { handler: TypedHandler<any>; router: TypedRouter<any> } | null {
    checkedRouters.push(this);
    const handler = this.handlerMap.findSync((h) => h.method === methodArg);
    if (handler) return { handler, router: this };
    for (const child of this.routerMap.getArray()) {
      if (!checkedRouters.includes(child)) {
        const result = child.getTypedHandlerAndRouter(methodArg, checkedRouters);
        if (result) return result;
      }
    }
    for (const fallback of this.fallbackRouterMap.getArray()) {
      if (!checkedRouters.includes(fallback)) {
        const result = fallback.getTypedHandlerAndRouter(methodArg, checkedRouters);
        if (result) return result;
      }
    }
    return null;
  }

  /**
   * Options for routeAndAddResponse
   */
  public static defaultRouteOptions = {
    localRequest: false,
    skipHooks: false,
  };

  /**
   * if typedrequest object has correlation.phase === 'request' -> routes a typed request object to a handler
   * if typedrequest object has correlation.phase === 'response' -> routes a typed request object to request fire event
   * @param typedRequestArg
   * @param optionsArg - Options object with:
   *   - localRequest: treat as local request (default: false)
   *   - skipHooks: skip calling hooks for this routing (default: false, use for broadcast-received messages)
   */
  public async routeAndAddResponse<
    T extends plugins.typedRequestInterfaces.ITypedRequest = plugins.typedRequestInterfaces.ITypedRequest
  >(typedRequestArg: T, optionsArg: ITypedRouterRouteOptions = {}): Promise<T> {
    if (!typedRequestArg || typeof typedRequestArg !== 'object') {
      return null as any;
    }

    let wireLocalDataPresent: boolean;
    try {
      wireLocalDataPresent = Object.prototype.propertyIsEnumerable.call(
        typedRequestArg,
        'localData',
      );
    } catch {
      return null as any;
    }
    const options = { ...TypedRouter.defaultRouteOptions, ...optionsArg };

    // Caller-controlled route data is never part of the trusted wire graph.
    if (!this.sanitizeIncomingLocalData(typedRequestArg)) {
      return null as any;
    }
    const trustedRouteLocalData = Object.freeze({
      ...(options.trustedLocalData ?? {}),
    });

    // A separately routed response remains raw. TypedRequest.fire() owns its
    // only decode/open pass and applies protocol-error precedence first.
    if (typedRequestArg?.correlation?.phase === 'response') {
      const responseInterestKey = getTypedRequestResponseInterestKey(typedRequestArg);
      if (!responseInterestKey) return null as any;
      if (this.incomingResponseAuthorityGuard) {
        const authorityContext = Object.freeze({
          typedRouter: this,
          trustedLocalData: trustedRouteLocalData,
          wireLocalDataPresent,
        });
        let authorized = false;
        try {
          authorized = this.incomingResponseAuthorityGuard(
            typedRequestArg,
            authorityContext,
          ) === true;
        } catch {}
        if (!authorized) return null as any;
      }
      if (!options.skipHooks) {
        this.callHook('onIncomingResponse', {
          correlationId: typedRequestArg.correlation.id,
          method: typedRequestArg.method,
          direction: 'incoming',
          phase: 'response',
          timestamp: Date.now(),
          payload: typedRequestArg.response,
          errorText: typedRequestArg.error?.text,
        });
      }
      this.fireEventInterestMap
        .findInterest(responseInterestKey)
        ?.fullfillInterest(typedRequestArg);
      return null as any;
    }
    if (
      typedRequestArg?.correlation?.phase !== 'request'
      || typeof typedRequestArg.correlation.id !== 'string'
      || typedRequestArg.correlation.id.length === 0
      || typeof typedRequestArg.method !== 'string'
      || typedRequestArg.method.length === 0
      || !typedRequestArg.request
      || typeof typedRequestArg.request !== 'object'
    ) {
      console.log('TypedRouter received a malformed request DTO');
      return null as any;
    }

    if (this.incomingRequestAuthorityGuard) {
      const authorityContext = Object.freeze({
        typedRouter: this,
        trustedLocalData: trustedRouteLocalData,
        wireLocalDataPresent,
      });
      let authorized = false;
      try {
        authorized = this.incomingRequestAuthorityGuard(
          typedRequestArg,
          authorityContext,
        ) === true;
      } catch {}
      if (!authorized) return null as any;
    }

    let requestSignalRegistration: ITypedRouterRequestSignalRegistration | undefined;
    let trustedAbortSignal = options.trustedAbortSignal;
    if (trustedAbortSignal !== undefined && !this.isAbortSignal(trustedAbortSignal)) {
      throw new TypeError('TypedRouter trustedAbortSignal must be an AbortSignal');
    }
    if (!trustedAbortSignal && this.incomingRequestSignalResolver) {
      if (
        typeof typedRequestArg.requestInstanceId !== 'string'
        || typedRequestArg.requestInstanceId.length === 0
      ) {
        return null as any;
      }
      const signalContext = Object.freeze({
        typedRouter: this,
        trustedLocalData: trustedRouteLocalData,
        wireLocalDataPresent,
      });
      try {
        requestSignalRegistration = this.incomingRequestSignalResolver(
          typedRequestArg,
          signalContext,
        );
      } catch {
        return null as any;
      }
      if (
        requestSignalRegistration
        && (
          !this.isAbortSignal(requestSignalRegistration.abortSignal)
          || typeof requestSignalRegistration.complete !== 'function'
        )
      ) {
        try {
          requestSignalRegistration.complete?.();
        } catch {}
        return null as any;
      }
      trustedAbortSignal = requestSignalRegistration?.abortSignal;
    }

    let virtualStreamTransport:
      | plugins.typedRequestInterfaces.IVirtualStreamTransport
      | undefined;
    try {
      virtualStreamTransport =
        this.virtualStreamTransportResolver?.(trustedRouteLocalData);
    } catch (error) {
      try {
        requestSignalRegistration?.complete();
      } catch {}
      throw error;
    }
    const requestActivationScope = new VirtualStreamActivationScope();
    const responseActivationScope = new VirtualStreamActivationScope();

    const rollbackRouteActivations = async (reasonArg?: unknown): Promise<void> => {
      await Promise.allSettled([
        requestActivationScope.rollback(reasonArg),
        responseActivationScope.rollback(reasonArg),
      ]);
    };

    const commitRouteActivations = (): void => {
      requestActivationScope.commit();
      responseActivationScope.commit();
    };

    const encodeResponse = async (): Promise<T> => {
      delete typedRequestArg.localData;
      return VirtualStream.encodePayloadForNetwork(
        typedRequestArg,
        {
          side: 'responding',
          virtualStreamTransport,
          activationScope: responseActivationScope,
        }
      ) as T;
    };

    try {
      typedRequestArg = await VirtualStream.decodePayloadFromNetworkAsync(
        typedRequestArg,
        {
          side: 'responding',
          virtualStreamTransport,
          activationScope: requestActivationScope,
          abortSignal: trustedAbortSignal,
        }
      );

      // Trusted transport context is attached only after wire graph decoding.
      typedRequestArg.localData = { ...trustedRouteLocalData };
      typedRequestArg.localData.firstTypedrouter = this;

      // lets do normal routing
      if (typedRequestArg?.correlation?.phase === 'request') {
        const requestStartTime = Date.now();

        // Hook: incoming request (skip if routing broadcast-received messages)
        if (!options.skipHooks) {
          this.callHook('onIncomingRequest', {
            correlationId: typedRequestArg.correlation?.id || 'unknown',
            method: typedRequestArg.method,
            direction: 'incoming',
            phase: 'request',
            timestamp: requestStartTime,
            payload: typedRequestArg.request,
          });
        }

        const result = this.getTypedHandlerAndRouter(typedRequestArg.method);

        if (!result) {
          console.log(`Cannot find handler for methodname ${typedRequestArg.method}`);
          typedRequestArg.error = {
            text: 'There is no available method for this call on the server side',
            data: {},
          };
          typedRequestArg.correlation!.phase = 'response';

          typedRequestArg = await encodeResponse();
          await rollbackRouteActivations(typedRequestArg.error);

          // Hook: outgoing response (error - no handler)
          if (!options.skipHooks) {
            this.callHook('onOutgoingResponse', {
              correlationId: typedRequestArg.correlation?.id || 'unknown',
              method: typedRequestArg.method,
              direction: 'outgoing',
              phase: 'response',
              timestamp: Date.now(),
              durationMs: Date.now() - requestStartTime,
              payload: typedRequestArg.response,
              errorText: typedRequestArg.error?.text,
            });
          }

          this.assertTrustedAbortSignalActive(trustedAbortSignal);
          return typedRequestArg;
        }

        const { handler: typedHandler, router: owningRouter } = result;

        // Run owning router's middleware chain
        if (owningRouter.middlewares.length > 0) {
          try {
            for (const mw of owningRouter.middlewares) {
              await mw(typedRequestArg as any);
            }
          } catch (e) {
            this.setErrorFromCaughtError(typedRequestArg, e, `middleware for method '${typedRequestArg.method}'`);
            typedRequestArg.correlation!.phase = 'response';
            typedRequestArg = await encodeResponse();
            await rollbackRouteActivations(e);

            if (!options.skipHooks) {
              this.callHook('onOutgoingResponse', {
                correlationId: typedRequestArg.correlation?.id || 'unknown',
                method: typedRequestArg.method,
                direction: 'outgoing',
                phase: 'response',
                timestamp: Date.now(),
                durationMs: Date.now() - requestStartTime,
                payload: typedRequestArg.response,
                errorText: typedRequestArg.error?.text,
              });
            }

            this.assertTrustedAbortSignalActive(trustedAbortSignal);
            return typedRequestArg;
          }
        }

        // Middleware can inspect staged facades but cannot initiate stream I/O.
        // The handler is the first application boundary allowed to use them.
        requestActivationScope.authorizeApplicationUse();
        typedRequestArg = await typedHandler.addResponse(
          typedRequestArg,
          trustedAbortSignal,
        );
        const handlerFailed = Boolean(typedRequestArg.error);
        typedRequestArg = await encodeResponse();
        if (handlerFailed) {
          await rollbackRouteActivations(typedRequestArg.error);
        }

        // Hook: outgoing response (success)
        if (!options.skipHooks) {
          this.callHook('onOutgoingResponse', {
            correlationId: typedRequestArg.correlation?.id || 'unknown',
            method: typedRequestArg.method,
            direction: 'outgoing',
            phase: 'response',
            timestamp: Date.now(),
            durationMs: Date.now() - requestStartTime,
            payload: typedRequestArg.response,
            errorText: typedRequestArg.error?.text,
          });
        }

        if (!handlerFailed) {
          this.assertTrustedAbortSignalActive(trustedAbortSignal);
          commitRouteActivations();
        }
        this.assertTrustedAbortSignalActive(trustedAbortSignal);
        return typedRequestArg;
      }

      await rollbackRouteActivations(
        new Error('TypedRouter received an unroutable request DTO')
      );
      this.assertTrustedAbortSignalActive(trustedAbortSignal);
      return null as any;
    } catch (error) {
      await rollbackRouteActivations(error);
      throw error;
    } finally {
      try {
        requestSignalRegistration?.complete();
      } catch {}
    }
  }

  private sanitizeIncomingLocalData(
    typedRequestArg: plugins.typedRequestInterfaces.ITypedRequest,
  ): boolean {
    try {
      const ownProperty = Reflect.getOwnPropertyDescriptor(
        typedRequestArg,
        'localData',
      );
      if (
        ownProperty
        && (
          ownProperty.configurable !== true
          || !Reflect.deleteProperty(typedRequestArg, 'localData')
        )
      ) {
        return false;
      }
      if (!Reflect.has(typedRequestArg, 'localData')) {
        return true;
      }
      if (!Reflect.isExtensible(typedRequestArg)) {
        return false;
      }
      if (!Reflect.defineProperty(typedRequestArg, 'localData', {
        configurable: true,
        enumerable: false,
        value: undefined,
        writable: true,
      })) {
        return false;
      }
      const maskProperty = Reflect.getOwnPropertyDescriptor(
        typedRequestArg,
        'localData',
      );
      return Boolean(
        maskProperty
        && maskProperty.configurable === true
        && maskProperty.enumerable === false
        && maskProperty.value === undefined
        && maskProperty.writable === true
      );
    } catch {
      return false;
    }
  }

  private isAbortSignal(valueArg: unknown): valueArg is AbortSignal {
    return Boolean(
      valueArg
      && typeof valueArg === 'object'
      && typeof Reflect.get(valueArg, 'aborted') === 'boolean'
      && typeof Reflect.get(valueArg, 'addEventListener') === 'function'
      && typeof Reflect.get(valueArg, 'removeEventListener') === 'function'
    );
  }

  private assertTrustedAbortSignalActive(signalArg?: AbortSignal): void {
    if (!signalArg?.aborted) return;
    throw signalArg.reason
      ?? new DOMException('The operation was aborted', 'AbortError');
  }

}
